// ==============================================
// Package form: the editable shape of a plan, its defaults, and how a saved
// Package loads into it.
// ==============================================

import type { TokenSettings } from '../PackageTokenSettings';
import type { BasemapSource, Package } from '../../../types/subscription';

// Available basemap sources with labels
export const BASEMAP_OPTIONS: { value: BasemapSource; label: string; description: string }[] = [
  { value: 'osm', label: 'OpenStreetMap', description: 'Free street map tiles' },
  { value: 'sentinel', label: 'Sentinel-2', description: 'Satellite imagery' },
  { value: 'esri', label: 'Esri World Imagery', description: 'High-res satellite (premium)' },
  { value: 'azure', label: 'Azure Maps', description: 'Microsoft satellite, dark & hybrid (requires API key)' },
  { value: 'google', label: 'Google Maps', description: 'Google Satellite & Hybrid' },
  { value: 'bing', label: 'Bing Maps', description: 'Bing Satellite imagery' },
  { value: 'mapbox', label: 'Mapbox Satellite', description: 'Mapbox aerial imagery (requires API key)' },
  { value: 'here', label: 'HERE Satellite', description: 'HERE aerial imagery (requires API key)' },
  { value: 'gebco', label: 'GEBCO', description: 'Bathymetry/depth charts' },
  { value: 'openseamap', label: 'OpenSeaMap', description: 'Nautical chart overlay' },
  { value: 'navionics', label: 'Navionics', description: 'Navionics/Garmin marine charts (view-only — ToS prohibits offline caching)' },
];

// Basemap sources available for offline downloads
// Note: Some sources prohibit tile caching per ToS, but admins can override for testing
export const OFFLINE_CAPABLE_SOURCES: BasemapSource[] = [
  'osm',      // OpenStreetMap - ODbL license allows caching (ToS compliant)
  'sentinel', // Sentinel-2 - ESA allows offline use (ToS compliant)
  'esri',     // Esri - allows caching with proper license (ToS compliant)
  'google',   // Google Maps - ToS prohibit caching (admin override for testing)
  'bing',     // Bing Maps - ToS prohibit caching (admin override for testing)
  'mapbox',   // Mapbox - ToS restrict caching (admin override for testing)
  'here',     // HERE - ToS restrict caching (admin override for testing)
  'gebco',    // GEBCO - public domain (ToS compliant)
  'openseamap', // OpenSeaMap - open license (ToS compliant)
];

export interface PackageFormData extends TokenSettings {
  name: string;
  slug: string;
  description: string;
  monthly_price_cents: number;
  included_tokens: number;
  cost_per_1000_tokens_cents: number;
  features: string[];
  priority_generation: boolean;
  api_access: boolean;
  weather_gfs: boolean;
  weather_ecmwf: boolean;
  weather_wavewatch: boolean;
  weather_briefing: boolean;
  forecast_duration_hours: number;
  ais_integration: boolean;
  bathymetry: boolean;
  vortex_status_panel: boolean;
  vortex_tide_prediction_days: number;
  vt_tide_alerts: boolean;
  siitech_marine_enc: boolean;
  nav_library: boolean;
  screenshot_library: boolean;
  screenshot_quota_mb: number;
  public_tracking: boolean;
  anchor_remote_view: boolean;
  external_nmea: boolean;
  tide_watch: boolean;
  instrument_panel: boolean;
  truehelm: boolean;
  vortex_anchor: boolean;
  sd_card_charts: boolean;
  google_drive_charts: boolean;
  sd_card_manager: boolean;
  document_library: boolean;
  document_quota_mb: number;
  app_library: boolean;
  google_webview: boolean;
  scout_diagnostics: boolean;
  weather_colour: boolean;
  weather_days: number;
  visibility: 'active' | 'hidden' | 'archived';
  sort_order: number;
  is_popular: boolean;
  manual_expiry: boolean;
  max_devices: number;
  max_zoom_level: number;
  allowed_basemaps: BasemapSource[];
  allowed_basemaps_offline: BasemapSource[];
  offline_packs: boolean;
}

export const emptyPackageForm: PackageFormData = {
  token_packs: [],
  out_of_tokens_policy: null,
  out_of_tokens_amount: null,
  name: '',
  slug: '',
  description: '',
  monthly_price_cents: 0,
  included_tokens: 0,
  cost_per_1000_tokens_cents: 0,
  features: [],
  priority_generation: false,
  api_access: false,
  weather_gfs: false,
  weather_ecmwf: false,
  weather_wavewatch: false,
  weather_briefing: false,
  forecast_duration_hours: 24,
  ais_integration: false,
  bathymetry: false,
  vortex_status_panel: false,
  vortex_tide_prediction_days: 1,
  vt_tide_alerts: false,
  siitech_marine_enc: false,
  nav_library: false,
  screenshot_library: false,
  screenshot_quota_mb: 2048,
  public_tracking: false,
  anchor_remote_view: false,
  external_nmea: true,
  tide_watch: false,
  instrument_panel: false,
  truehelm: false,
  vortex_anchor: false,
  sd_card_charts: false,
  google_drive_charts: false,
  sd_card_manager: false,
  document_library: false,
  document_quota_mb: 2048,
  app_library: false,
  google_webview: false,
  scout_diagnostics: false,
  weather_colour: false,
  weather_days: 2,
  visibility: 'active',
  sort_order: 0,
  is_popular: false,
  manual_expiry: false,
  max_devices: 1,
  max_zoom_level: 14,
  allowed_basemaps: ['osm', 'gebco', 'openseamap'],
  // Never default to [] — a saved empty list means zero offline sources and
  // bypasses the server's NULL→['osm'] fallback.
  allowed_basemaps_offline: ['osm'],
  offline_packs: true,
};

export function packageToForm(pkg: Package): PackageFormData {
  return {
    name: pkg.name,
    slug: pkg.slug,
    description: pkg.description || '',
    monthly_price_cents: pkg.monthly_price_cents,
    included_tokens: pkg.included_tokens,
    cost_per_1000_tokens_cents: pkg.cost_per_1000_tokens_cents,
    features: pkg.features || [],
    priority_generation: pkg.priority_generation || false,
    api_access: pkg.api_access || false,
    weather_gfs: pkg.weather_gfs || false,
    weather_ecmwf: pkg.weather_ecmwf || false,
    weather_wavewatch: pkg.weather_wavewatch || false,
    weather_briefing: pkg.weather_briefing || false,
    forecast_duration_hours: pkg.forecast_duration_hours || 24,
    ais_integration: pkg.ais_integration || false,
    bathymetry: pkg.bathymetry || false,
    vortex_status_panel: pkg.vortex_status_panel || false,
    vortex_tide_prediction_days: pkg.vortex_tide_prediction_days ?? 1,
    vt_tide_alerts: pkg.vt_tide_alerts || false,
    siitech_marine_enc: pkg.siitech_marine_enc || false,
    nav_library: pkg.nav_library || false,
    screenshot_library: pkg.screenshot_library || false,
    screenshot_quota_mb: pkg.screenshot_quota_mb ?? 2048,
    public_tracking: pkg.public_tracking || false,
    anchor_remote_view: pkg.anchor_remote_view || false,
    // Every plan had the boat's instruments before this switch existed.
    external_nmea: pkg.external_nmea ?? true,
    tide_watch: pkg.tide_watch || false,
    instrument_panel: pkg.instrument_panel || false,
    truehelm: pkg.truehelm || false,
    vortex_anchor: pkg.vortex_anchor || false,
    sd_card_charts: pkg.sd_card_charts || false,
    google_drive_charts: pkg.google_drive_charts || false,
    sd_card_manager: pkg.sd_card_manager || false,
    document_library: pkg.document_library || false,
    document_quota_mb: pkg.document_quota_mb ?? 2048,
    app_library: pkg.app_library || false,
    google_webview: pkg.google_webview || false,
    scout_diagnostics: pkg.scout_diagnostics || false,
    weather_colour: pkg.weather_colour || false,
    weather_days: pkg.weather_days ?? 2,
    visibility: pkg.visibility || 'active',
    sort_order: pkg.sort_order || 0,
    is_popular: pkg.is_popular || false,
    manual_expiry: pkg.manual_expiry || false,
    token_packs: pkg.token_packs ?? [],
    out_of_tokens_policy: pkg.out_of_tokens_policy ?? null,
    out_of_tokens_amount: pkg.out_of_tokens_amount ?? null,
    max_devices: pkg.max_devices ?? 1,
    max_zoom_level: pkg.max_zoom_level ?? 14,
    allowed_basemaps: pkg.allowed_basemaps || ['osm', 'gebco', 'openseamap'],
    // ['osm'] mirrors the server's NULL fallback; loading [] here would
    // persist zero offline sources on the next save.
    allowed_basemaps_offline: pkg.allowed_basemaps_offline?.length
      ? pkg.allowed_basemaps_offline
      : ['osm'],
    offline_packs: pkg.offline_packs ?? true,
  };
}
